import { getSessionId } from '@onefootprint/dev-tools';
import { getErrorCode, getErrorMessage } from '@onefootprint/fetch-hosted';
import { IcoInfo16 } from '@onefootprint/icons';
import type { ApiError } from '@onefootprint/request-types';
import { Button, CodeInline, PageShell } from '@onefootprint/ui';
import { useQueryClient } from '@tanstack/react-query';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/idv2/features/app-shell';
import { useAppNavigate } from '@/idv2/hooks/use-app-navigate';
import { getLogger } from '@/idv2/utils/logger';
import { UnsupportedRequirementError } from '@/idv2/utils/unsupported-requirement-error';
const {
  logError,
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'route-error-boundary'
});
type ErrorKey = 'connectionError' | 'genericError' | 'sessionNotFound' | 'sessionExpired' | 'sessionInvalid' | 'businessNotOwnedByUser' | 'onboardingSuperseded' | 'unsupportedRequirement';
type ErrorMap = Record<ErrorKey, {
  title: string;
  subtitle: string;
  type: ErrorKey;
}>;

// onboardingSuperseded is a race between two concurrent onboarding sessions, not a server fault.
const ERROR_TYPES_TO_IGNORE: ErrorKey[] = ['connectionError', 'sessionExpired', 'businessNotOwnedByUser', 'onboardingSuperseded'];
const CONNECTION_ERROR_MESSAGES = ['failed to fetch', 'load failed', 'networkerror when attempting to fetch'];
const ERROR_CODE_MAP: Record<string, ErrorKey> = {
  E117: 'sessionNotFound',
  E118: 'sessionExpired',
  E119: 'sessionInvalid',
  E124: 'businessNotOwnedByUser',
  E140: 'onboardingSuperseded'
};

/**
 * The root route's errorComponent. The AppShell is here because a superseded session throws from
 * RootComponent itself, so this stands in for the shell rather than rendering inside it.
 */
export const RouteErrorBoundary = ({
  error
}: ErrorComponentProps) => {
  const {
    t
  } = useTranslation('idv2', {
    keyPrefix: 'errorFallback'
  });
  const location = useLocation();
  const sessionId = getSessionId();
  const {
    forward,
    close
  } = useAppNavigate();
  const errorKey = getErrorKey(error);
  const queryClient = useQueryClient();
  const [isRetrying, setIsRetrying] = useState(false);
  const errorMap: ErrorMap = {
    connectionError: {
      title: t($ => $.connectionError.title),
      subtitle: t($_0 => $_0.connectionError.subtitle),
      type: 'connectionError'
    },
    genericError: {
      title: t($_1 => $_1.genericError.title),
      subtitle: t($_2 => $_2.genericError.subtitle),
      type: 'genericError'
    },
    sessionNotFound: {
      title: t($_3 => $_3.sessionNotFound.title),
      subtitle: t($_4 => $_4.sessionNotFound.subtitle),
      type: 'sessionNotFound'
    },
    sessionExpired: {
      title: t($_5 => $_5.sessionExpired.title),
      subtitle: t($_6 => $_6.sessionExpired.subtitle),
      type: 'sessionExpired'
    },
    sessionInvalid: {
      title: t($_7 => $_7.sessionInvalid.title),
      subtitle: t($_8 => $_8.sessionInvalid.subtitle),
      type: 'sessionInvalid'
    },
    businessNotOwnedByUser: {
      title: t($_9 => $_9.businessNotOwnedByUser.title),
      subtitle: t($_10 => $_10.businessNotOwnedByUser.subtitle),
      type: 'businessNotOwnedByUser'
    },
    onboardingSuperseded: {
      title: t($_11 => $_11.onboardingSuperseded.title),
      subtitle: t($_12 => $_12.onboardingSuperseded.subtitle),
      type: 'onboardingSuperseded'
    },
    unsupportedRequirement: {
      title: t($_13 => $_13.unsupportedRequirement.title),
      subtitle: t($_14 => $_14.unsupportedRequirement.subtitle),
      type: 'unsupportedRequirement'
    }
  };
  const {
    title,
    subtitle,
    type
  } = errorMap[errorKey];
  useEffect(() => {
    const apiError = (error && typeof error === 'object' ? error : {}) as Partial<ApiError> & {
      routerCode?: string;
    };
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const unsupportedRequirement = error instanceof UnsupportedRequirementError ? error : undefined;
    const diagnostics = {
      route: location.pathname,
      type,
      errorSupportId: apiError.supportId,
      routerCode: apiError.routerCode,
      isIframed: window.self !== window.top,
      ancestorOrigin: window.location.ancestorOrigins?.[0],
      navigationType: navigationEntry?.type,
      requirementKind: unsupportedRequirement?.requirementKind,
      orgId: unsupportedRequirement?.orgId
    };
    if (!ERROR_TYPES_TO_IGNORE.includes(type)) {
      logError(`Route error boundary caught ${type}:`, error, diagnostics);
    } else {
      logWarn(`Route error boundary caught ${type}:`, error, diagnostics);
    }
  }, [error, location.pathname, type]);
  const onRetry = async () => {
    logInfo('User clicked retry button', {
      error: getErrorMessage(error)
    });
    setIsRetrying(true);
    // removeQueries() deletes cached state synchronously, forcing the next
    // ensureQueryData in beforeLoad to re-run its queryFn. resetQueries races a
    // background refetch with the navigation, which can leave a stale error in place.
    queryClient.removeQueries();
    try {
      await forward();
    } finally {
      setIsRetrying(false);
    }
  };
  const handleClose = () => {
    logInfo('User clicked close button', {
      error: getErrorMessage(error)
    });
    close?.();
  };
  return <AppShell showCloseButton isErrorScreen>
      <PageShell title={title} subtitle={subtitle}>
        {(type === 'genericError' || type === 'unsupportedRequirement') && <div className="flex flex-col items-center gap-4 p-6 mt-4 border border-tertiary rounded-md">
            <div className="flex items-center gap-1">
              <IcoInfo16 color="primary" />
              <span className="text-label-2 text-primary">{t($_15 => $_15.sessionId)}</span>
            </div>
            <span className="text-body-2 text-center text-primary">{t($_16 => $_16.sessionIdHint)}</span>
            <CodeInline>{sessionId}</CodeInline>
          </div>}
        {type === 'connectionError' || type === 'genericError' ? <Button onClick={onRetry} loading={isRetrying} fullWidth size="large" className="mt-6">
            {t($_17 => $_17.tryAgain)}
          </Button> : <Button onClick={handleClose} fullWidth size="large" className="mt-6">
            {t($_18 => $_18.close)}
          </Button>}
      </PageShell>
    </AppShell>;
};
const getErrorKey = (error: unknown): ErrorKey => {
  if (error instanceof UnsupportedRequirementError) {
    return 'unsupportedRequirement';
  }
  const errorMessage = getErrorMessage(error).toLowerCase();
  if (CONNECTION_ERROR_MESSAGES.some(message => errorMessage.includes(message))) {
    return 'connectionError';
  }

  // 404s in beforeLoad are transient: our backend's actix wildcard returns
  // "Endpoint not found" for any unrecognised method/path combination, which can
  // happen when an iOS Safari prefetch / network probe issues a GET against a
  // POST-only endpoint. Treat as connectionError so the boundary stays in
  // ERROR_TYPES_TO_IGNORE and the user sees a retryable screen instead of
  // "Something went wrong".
  const status = (error as {
    response?: {
      status?: number;
    };
  } | null)?.response?.status;
  if (status === 404) {
    return 'connectionError';
  }
  const errorCode = getErrorCode(error);
  if (errorCode && errorCode in ERROR_CODE_MAP) {
    return ERROR_CODE_MAP[errorCode];
  }
  return 'genericError';
};